import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy, addDoc, updateDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { DateTime } from 'luxon';
import { db } from '../firebase';
import { resolveZone, slotUtcToDate, utcMinToLocalParts } from '../dateUtils';
import ReportUser from './ReportUser';
import ScheduleSheet, { commonSlotsOf } from './ScheduleSheet';
import MoreMenu from './MoreMenu';
import { startCall } from './VideoCall';
import { Icon, Avatar } from './ui';
import { examLabel } from '../names';
import { sendFailToast } from '../sendErrors';
import { useBlocked, withoutBlocked, confirmBlock } from '../blocking';
import { useBackClose } from '../backStack';
import { tapFeedback } from '../native';

// List times: "17:42" today, "Tue" this week, then "Oct 29".
export const listTime = (d, now = new Date()) => {
  if (!d) return '';
  const t = DateTime.fromJSDate(d), n = DateTime.fromJSDate(now);
  if (t.hasSame(n, 'day')) return t.toFormat('HH:mm');
  if (n.diff(t, 'days').days < 6) return t.toFormat('ccc');
  return t.hasSame(n, 'year') ? t.toFormat('LLL d') : t.toFormat('LLL d, yyyy');
};

// Chat date separators: "Today", "Yesterday", "Tue, Oct 27".
export const dayLabel = (d, now = new Date()) => {
  const t = DateTime.fromJSDate(d), n = DateTime.fromJSDate(now);
  if (t.hasSame(n, 'day')) return 'Today';
  if (t.hasSame(n.minus({ days: 1 }), 'day')) return 'Yesterday';
  return t.hasSame(n, 'year') ? t.toFormat('ccc, LLL d') : t.toFormat('ccc, LLL d, yyyy');
};

// The chat header's subtitle: the next time we're both free, else their exam.
const partnerLine = (me, them, zone) => {
  if (!them) return '';
  const next = commonSlotsOf(me, them)
    .map(s => ({ s, when: slotUtcToDate(s) }))
    .filter(x => x.when)
    .sort((a, b) => a.when - b.when)[0];
  if (next) { const p = utcMinToLocalParts(next.s.startMin, zone); return `Free together ${p.dayShort} ${p.time}`; }
  return examLabel(them.studyCategory);
};

// True at 1024 px and wider.
function useWide() {
  const q = '(min-width: 1024px)';
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return undefined;
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

function Messages({ user, userProfile, showNotification, initialConvo, onChatChange, onOpenDocs, onFindMatches }) {
  const [allMessages, setMessages] = useState([]);
  const [activeConvo, setActiveConvo] = useState(initialConvo || null);
  const [convoMsgs, setConvoMsgs] = useState([]);
  const [everyConvo, setAllConvo] = useState([]);
  // Nothing from people I've blocked: no threads, unread dots or requests.
  const blocked = useBlocked(user?.uid);
  const messages = useMemo(() => withoutBlocked(allMessages, blocked, m => m.fromUid), [allMessages, blocked]);
  const allConvo = useMemo(() => withoutBlocked(everyConvo, blocked, c => c.fromUid === user.uid ? c.toUid : c.fromUid), [everyConvo, blocked, user.uid]);
  const [newMsg, setNewMsg] = useState('');
  const wide = useWide();
  const [reportModal, setReportModal] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [partner, setPartner] = useState(null);
  const [photos, setPhotos] = useState({});
  const endRef = useRef(null);
  const listRef = useRef(null);
  const atBottom = useRef(true);
  const intentRead = useRef(false);
  const zone = resolveZone(userProfile?.timezone);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db,'messages'), where('toUid','==',user.uid), orderBy('createdAt','desc'));
    return onSnapshot(q, snap => setMessages(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [user?.uid]);

  // Every chat line I'm part of, both directions; the list is built from these.
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db,'conversations'), where('participants','array-contains',user.uid), orderBy('createdAt','asc'));
    return onSnapshot(q, snap => setAllConvo(snap.docs.map(d=>({id:d.id,...d.data()}))), err => console.error('Conversation list failed:', err.code, err.message));
  }, [user?.uid]);

  // Fetch each distinct person's photo once, then reuse it.
  useEffect(() => {
    const uids = new Set();
    messages.forEach(m => m.fromUid && uids.add(m.fromUid));
    convoMsgs.forEach(m => m.fromUid && uids.add(m.fromUid));
    allConvo.forEach(c => { c.fromUid && uids.add(c.fromUid); c.toUid && uids.add(c.toUid); });
    if (activeConvo?.uid) uids.add(activeConvo.uid);
    const missing = [...uids].filter(u => u !== user.uid && !(u in photos));
    if (!missing.length) return;
    (async () => {
      const found = {};
      for (const uid of missing) {
        try {
          const snap = await getDoc(doc(db,'users',uid));
          found[uid] = snap.exists() ? (snap.data().profilePicture || '') : '';
        } catch (e) { found[uid] = ''; }
      }
      setPhotos(prev => ({ ...prev, ...found }));
    })();
  }, [messages, convoMsgs, allConvo, activeConvo, user.uid, photos]);

  useEffect(() => {
    if (!activeConvo) return;
    const q = query(collection(db,'conversations'), where('participants','array-contains',user.uid), orderBy('createdAt','asc'));
    return onSnapshot(q, snap => {
      const msgs = snap.docs.map(d=>({id:d.id,...d.data()})).filter(m=>(m.fromUid===user.uid&&m.toUid===activeConvo.uid)||(m.fromUid===activeConvo.uid&&m.toUid===user.uid));
      setConvoMsgs(msgs);
      setTimeout(()=>endRef.current?.scrollIntoView({behavior:'smooth'}),100);
    }, err => console.error('Conversation listener failed:', err.code, err.message));
  }, [activeConvo, user?.uid]);

  useEffect(() => { onChatChange && onChatChange(!!activeConvo); }, [activeConvo, onChatChange]);

  // Back (or the iPhone's edge swipe) closes the chat.
  useBackClose(!!activeConvo, () => setActiveConvo(null));

  // The open chat's partner, for the header subtitle and the photo.
  useEffect(() => {
    setPartner(null);
    if (!activeConvo?.uid) return;
    let live = true;
    getDoc(doc(db,'users',activeConvo.uid)).then(snap => live && setPartner(snap.data() || {})).catch(() => {});
    return () => { live = false; };
  }, [activeConvo?.uid]);

  // Blocking someone (here, from Report, or on another device) closes their chat.
  useEffect(() => { if (activeConvo && activeConvo.uid in blocked) setActiveConvo(null); }, [activeConvo, blocked]);

  // Keep the newest message in view when the list resizes (e.g. the phone keyboard opens).
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { if (atBottom.current) el.scrollTop = el.scrollHeight; });
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeConvo]);

  const photoOf = (uid) => uid === user.uid ? (userProfile?.profilePicture || '') : (photos[uid] || '');

  const markRead = async (id) => { try { await updateDoc(doc(db,'messages',id),{read:true}); } catch(e){} };

  // Opened from a notification: mark that person's messages read once they load.
  useEffect(() => {
    if (!initialConvo || intentRead.current || !messages.length) return;
    intentRead.current = true;
    messages.filter(m => m.fromUid === initialConvo.uid && !m.read).forEach(m => markRead(m.id));
  }, [messages, initialConvo]);

  // While a chat is open, anything that person sends counts as read as soon as it arrives.
  useEffect(() => {
    if (!activeConvo) return;
    messages.filter(m => m.fromUid === activeConvo.uid && !m.read).forEach(m => markRead(m.id));
  }, [messages, activeConvo]);

  const openConvo = async (fromUid, fromName) => {
    messages.filter(m => m.fromUid === fromUid && !m.read).forEach(m => markRead(m.id));
    setActiveConvo({ uid: fromUid, name: fromName });
  };

  const deliver = async (toUid, toName, content) => {
    const from = userProfile?.name || 'A study partner';
    await addDoc(collection(db,'conversations'),{
      fromUid:user.uid, fromName:from, toUid, toName, content,
      participants:[user.uid,toUid], createdAt:serverTimestamp()
    });
    await addDoc(collection(db,'messages'),{
      fromUid:user.uid, fromName:from, toUid, toName, content,
      read:false, createdAt:serverTimestamp()
    });
    try {
      const recipSnap = await getDoc(doc(db,'users',toUid));
      if (recipSnap.exists() && recipSnap.data()?.notificationPreferences?.newMessages !== false) {
        await addDoc(collection(db,'notifications'),{
          toUid, fromUid:user.uid, fromName:from,
          type:'message', text:`${from} sent you a message`,
          read:false, createdAt:serverTimestamp()
        });
      }
    } catch(e) { console.warn('notif write failed',e); }
  };

  const sendConvoMsg = async () => {
    if (!newMsg.trim() || !activeConvo) return;
    const text = newMsg;
    setNewMsg('');
    try {
      tapFeedback();
      await deliver(activeConvo.uid, activeConvo.name, text);
    } catch(e) { console.error(e); setNewMsg(text); sendFailToast('message', e, 'Error sending message.'); }
  };

  const dateOf = ts => (ts?.toDate ? ts.toDate() : null);
  const fmt = ts => listTime(dateOf(ts)) || 'Just now';

  let chat = null;
  if (activeConvo) {
    const subtitle = partnerLine(userProfile, partner, zone);
    // One time per group: shown under the last line of a run from the same person
    // within five minutes. A date separator starts each new day.
    let lastDay = null;
    const rows = convoMsgs.map((m, i) => {
      const d = dateOf(m.createdAt);
      const next = convoMsgs[i + 1];
      const nd = next && dateOf(next.createdAt);
      const endOfGroup = !next || next.fromUid !== m.fromUid || !d || !nd || nd - d > 5 * 60000;
      const day = d ? dayLabel(d) : 'Today';
      const sep = day !== lastDay ? day : null;
      lastDay = day;
      return { m, sep, time: endOfGroup ? (d ? DateTime.fromJSDate(d).toFormat('HH:mm') : 'Just now') : null };
    });
    chat = (
    <>
      <div className="conversation-container">
        <div className="conversation-header">
          <button className="btn-icon small plain conv-back" onClick={()=>setActiveConvo(null)} aria-label="Back to messages"><Icon name="back" /></button>
          <Avatar src={photoOf(activeConvo.uid)} name={activeConvo.name} size={40} viewable />
          <div className="conversation-title">
            <h2>{activeConvo.name}</h2>
            {subtitle && <p className="muted small">{subtitle}</p>}
          </div>
          <button className="btn-icon small plain" onClick={()=>setScheduling(true)} aria-label={`Schedule a session with ${activeConvo.name}`}><Icon name="calendar" /></button>
          <button className="btn-icon small plain" onClick={()=>startCall(activeConvo.uid, activeConvo.name)} aria-label={`Video call with ${activeConvo.name}`}><Icon name="video" /></button>
          <MoreMenu label={`More options for ${activeConvo.name}`} items={[
            ...(onOpenDocs ? [{ label: 'Shared docs', icon: 'docs', onClick: onOpenDocs }] : []),
            { label: 'Report', icon: 'flag', danger: true, onClick: () => setReportModal({ uid: activeConvo.uid, name: activeConvo.name }) },
            { label: 'Block', icon: 'block', danger: true, onClick: () => confirmBlock(user.uid, activeConvo) },
          ]} />
        </div>
        <div className="conversation-messages" ref={listRef} onScroll={e=>{const el=e.currentTarget;atBottom.current=el.scrollHeight-el.scrollTop-el.clientHeight<40;}}>
          {convoMsgs.length===0 && <div className="empty-state" style={{padding:'32px 0'}}>No messages yet. Say hi!</div>}
          {rows.map(({ m, sep, time })=>{
            const mine = m.fromUid===user.uid;
            return (
              <React.Fragment key={m.id}>
                {sep && <div className="chat-day">{sep}</div>}
                <div className={`bubble-row ${mine?'me':'them'} ${time?'':'grouped'}`}>
                  <div className={`message-bubble ${mine?'me':'them'}`}>
                    <div className="message-text">{m.content}</div>
                  </div>
                  {time && <div className="message-time">{time}</div>}
                </div>
              </React.Fragment>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="message-input-container">
          <textarea placeholder="Type your message..." aria-label="Message" value={newMsg} onChange={e=>setNewMsg(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendConvoMsg();}}} />
          <button className="btn-icon dark" onClick={sendConvoMsg} disabled={!newMsg.trim()} aria-label="Send"><Icon name="send" /></button>
        </div>
      </div>

      {scheduling && (
        <ScheduleSheet user={user} userProfile={userProfile} partner={{ uid: activeConvo.uid, name: activeConvo.name }} onClose={()=>setScheduling(false)} />
      )}
      {reportModal && (
        <ReportUser
          user={user}
          userProfile={userProfile}
          reported={reportModal}
          onClose={()=>setReportModal(null)}
          showNotification={showNotification}
        />
      )}
    </>
    );
  }

  // One row per person, from the conversation lines (both directions) plus inbox
  // messages that never got a conversation copy (first messages sent from Matches).
  const ms = ts => ts?.toMillis ? ts.toMillis() : Date.now();
  const byPartner = new Map();
  const consider = (other, line, mine) => {
    if (!other) return;
    const cur = byPartner.get(other);
    if (!cur || ms(line.createdAt) >= ms(cur.createdAt)) {
      byPartner.set(other, { fromUid: other, fromName: (mine ? line.toName : line.fromName) || cur?.fromName || 'Study partner', content: line.content, createdAt: line.createdAt, mine });
    }
  };
  allConvo.forEach(c => { const mine = c.fromUid === user.uid; consider(mine ? c.toUid : c.fromUid, c, mine); });
  messages.forEach(m => consider(m.fromUid, m, false));
  const threads = [...byPartner.values()]
    .map(t => ({ ...t, unread: messages.some(m => m.fromUid === t.fromUid && !m.read) }))
    .sort((a, b) => ms(b.createdAt) - ms(a.createdAt));

  const list = (
    <div className="card thread-card">
      {threads.length===0 ? <div className="empty-state compact"><div className="empty-icon"><Icon name="messages" size={32} /></div><p className="small">No messages yet. Say hi to a match to start.</p>{onFindMatches && <button className="btn-small secondary" style={{marginTop:'16px'}} onClick={onFindMatches}>Find a partner</button>}</div> : (
        <div className="thread-list">
          {threads.map(t=>(
            <button key={t.fromUid} className={`thread-row thread-link ${t.unread?'unread':''} ${activeConvo?.uid===t.fromUid?'active':''}`} aria-current={activeConvo?.uid===t.fromUid ? 'true' : undefined} onClick={()=>openConvo(t.fromUid,t.fromName)}>
              <Avatar src={photoOf(t.fromUid)} name={t.fromName} size={48} />
              <div className="thread-body">
                <div className="message-header">
                  <div className="sender-name">{t.fromName}</div>
                  <div className="thread-meta"><span className="timestamp">{fmt(t.createdAt)}</span>{t.unread&&<span className="unread-dot" role="img" aria-label="Unread" />}</div>
                </div>
                <div className="message-content thread-preview">{t.mine ? `You: ${t.content}` : t.content}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Two panes from 1024 px (design F28): the list on the left, the chat on the right.
  if (wide) return (
    <div className="messages-split">
      {list}
      <div className="messages-pane">
        {chat || <div className="card empty-state messages-pick"><div className="empty-icon"><Icon name="messages" size={40} /></div><p className="small">Choose a conversation.</p></div>}
      </div>
    </div>
  );
  return chat || list;
}
export default Messages;
